/**
 * Meta Pixel + Conversions API helpers for PepDrive365.
 * Mirrors myfastrx: browser Purchase + POST processPayment_test with the same eventId (= orderId).
 */

import { processPayment_test } from "./Constant";

export const META_PIXEL_ID = "1574437137608068";

const META_PURCHASE_KEY_PREFIX = "meta_purchase_";
const META_PENDING_PURCHASE_KEY = "meta_pending_purchase";
const FBCLID_SESSION_KEY = "fbclid";
const FBC_FIRST_SEEN_MS_KEY = "fbc_first_seen_ms";

/**
 * Canonical event_id for Pixel eventID and CAPI eventId.
 * Same as myfastrx: raw order id (e.g. "405753"), not "order_405753".
 */
export function getMetaPurchaseEventId(orderId) {
  if (orderId == null || orderId === "") return "";
  return String(orderId);
}

export function getCookie(name) {
  if (typeof document === "undefined") return "";
  try {
    const match = document.cookie.match(
      new RegExp(
        `(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1")}=([^;]*)`
      )
    );
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
}

/** Persist fbclid from URL for fbc fallback (same as myfastrx). */
export function captureFbclid() {
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("fbclid") || "";
    const stored = sessionStorage.getItem(FBCLID_SESSION_KEY) || "";

    if (fromUrl) {
      sessionStorage.setItem(FBCLID_SESSION_KEY, fromUrl);
      if (
        fromUrl !== stored ||
        !sessionStorage.getItem(FBC_FIRST_SEEN_MS_KEY)
      ) {
        sessionStorage.setItem(FBC_FIRST_SEEN_MS_KEY, String(Date.now()));
      }
      return fromUrl;
    }
    return stored;
  } catch {
    return "";
  }
}

/**
 * Prefer Meta _fbc cookie; else build fb.1.1.{ms}.{fbclid} like myfastrx.
 */
export function getFbcForEvents() {
  const fromCookie = (getCookie("_fbc") || "").trim();
  if (fromCookie) return fromCookie;

  const fbclid = captureFbclid();
  if (!fbclid) return "";

  let firstMs = null;
  try {
    firstMs = sessionStorage.getItem(FBC_FIRST_SEEN_MS_KEY);
    if (!firstMs) {
      firstMs = String(Date.now());
      sessionStorage.setItem(FBC_FIRST_SEEN_MS_KEY, firstMs);
    }
  } catch {
    firstMs = String(Date.now());
  }

  return `fb.1.1.${firstMs}.${fbclid}`;
}

export function getMetaClickIds() {
  captureFbclid();
  return {
    fbp: getCookie("_fbp") || "",
    fbc: getFbcForEvents(),
  };
}

export function savePendingMetaPurchase(payload) {
  if (typeof window === "undefined" || !payload?.orderId) return;
  try {
    sessionStorage.setItem(META_PENDING_PURCHASE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function loadPendingMetaPurchase() {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(META_PENDING_PURCHASE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingMetaPurchase() {
  try {
    sessionStorage.removeItem(META_PENDING_PURCHASE_KEY);
  } catch {
    /* ignore */
  }
}

function normalizeEmail(email) {
  return String(email || "")
    .trim()
    .toLowerCase();
}

function normalizePhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `1${digits}`;
  return digits;
}

function normalizeName(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeCity(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function normalizeState(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function normalizeZip(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function normalizeCountry(value) {
  const c = String(value || "")
    .trim()
    .toLowerCase();
  if (!c) return "us";
  if (c === "united states" || c === "usa" || c === "u.s." || c === "u.s.a.") {
    return "us";
  }
  return c.length === 2 ? c : c;
}

async function sha256Hex(value) {
  if (!value || typeof window === "undefined" || !window.crypto?.subtle) {
    return "";
  }
  const data = new TextEncoder().encode(value);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** SHA-256 after Meta normalization (for Pixel Advanced Matching / beacon). */
async function hashEmailForMeta(email) {
  const normalized = normalizeEmail(email);
  return normalized ? sha256Hex(normalized) : "";
}

async function hashPhoneForMeta(phone) {
  const normalized = normalizePhone(phone);
  return normalized ? sha256Hex(normalized) : "";
}

function metaPurchaseAlreadyFired(eventId) {
  try {
    return !!sessionStorage.getItem(`${META_PURCHASE_KEY_PREFIX}${eventId}`);
  } catch {
    return false;
  }
}

function markMetaPurchaseFired(eventId) {
  try {
    sessionStorage.setItem(`${META_PURCHASE_KEY_PREFIX}${eventId}`, "1");
  } catch {
    /* ignore */
  }
}

function waitForFbq(timeoutMs = 5000) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("No window"));
  }
  if (typeof window.fbq === "function") return Promise.resolve(window.fbq);

  return new Promise((resolve, reject) => {
    const started = Date.now();
    const poll = setInterval(() => {
      if (typeof window.fbq === "function") {
        clearInterval(poll);
        resolve(window.fbq);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        clearInterval(poll);
        reject(new Error("fbq unavailable"));
      }
    }, 50);
  });
}

/**
 * Explicit facebook.com/tr Purchase beacon — always visible in Network as ev=Purchase.
 * Same event_id as fbq + CAPI for dedupe. Complements (does not replace) fbq('track').
 */
function fireMetaPurchaseBeacon({
  eventId,
  amount,
  currency,
  contentIds,
  fbp,
  fbc,
  hashedEmail,
  hashedPhone,
}) {
  if (typeof document === "undefined") return "";

  const params = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: "Purchase",
    eid: String(eventId),
    noscript: "1",
  });
  params.set("cd[value]", String(amount));
  params.set("cd[currency]", currency || "USD");
  params.set("cd[content_type]", "product");
  if (contentIds?.length) {
    params.set("cd[content_ids]", JSON.stringify(contentIds.map(String)));
  }
  if (hashedEmail) params.set("ud[em]", hashedEmail);
  if (hashedPhone) params.set("ud[ph]", hashedPhone);
  if (fbp) params.set("fbp", fbp);
  if (fbc) params.set("fbc", fbc);

  const url = `https://www.facebook.com/tr/?${params.toString()}`;

  try {
    const img = new Image();
    img.src = url;
  } catch {
    /* ignore */
  }

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
    }
  } catch {
    /* ignore */
  }

  console.log(
    "[Meta] Purchase beacon sent — Network filter: tr  (ev=Purchase & eid=" +
      eventId +
      ")",
    {
      eventID: eventId,
      hasHashedEmail: !!hashedEmail,
      hasHashedPhone: !!hashedPhone,
      hasFbp: !!fbp,
      hasFbc: !!fbc,
    }
  );
  return url;
}

/**
 * Server-side CAPI via panel relay (same payload shape as myfastrx).
 * Panel expects plaintext email/phone; it hashes server-side.
 */
async function sendMetaCapiPurchase({
  eventId,
  amount,
  currency,
  email,
  phone,
  contentIds,
  contentName,
  eventSourceUrl,
}) {
  const { fbp, fbc } = getMetaClickIds();
  const sourceUrl =
    eventSourceUrl ||
    (typeof window !== "undefined" ? window.location.href : "");

  const capiPayload = {
    eventId: String(eventId),
    amount: Number(amount),
    currency: currency || "USD",
    event_source_url: String(sourceUrl).includes("localhost")
      ? "https://checkout.pepdrive365.com/thankyou-page"
      : sourceUrl,
  };

  if (contentName) capiPayload.content_name = contentName;
  if (contentIds?.length) {
    capiPayload.content_ids = contentIds.map(String);
  }

  const em = normalizeEmail(email);
  if (em) capiPayload.email = em;

  const phoneDigits = String(phone || "").replace(/\D/g, "");
  if (phoneDigits) capiPayload.phone = phoneDigits;

  if (fbc) capiPayload.fbc = fbc;
  if (fbp) capiPayload.fbp = fbp;

  console.log("[Meta CAPI] Sending processPayment_test:", {
    eventId: capiPayload.eventId,
    amount: capiPayload.amount,
    currency: capiPayload.currency,
    hasEmail: !!capiPayload.email,
    hasPhone: !!capiPayload.phone,
    hasFbc: !!capiPayload.fbc,
    hasFbp: !!capiPayload.fbp,
    url: processPayment_test,
  });

  const response = await fetch(processPayment_test, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(capiPayload),
  });

  if (response.ok) {
    console.log("[Meta CAPI] processPayment_test OK", {
      eventId: capiPayload.eventId,
      status: response.status,
    });
    return true;
  }

  const errorText = await response.text().catch(() => "");
  console.warn("[Meta CAPI] processPayment_test failed", {
    status: response.status,
    error: errorText,
    eventId: capiPayload.eventId,
  });
  return false;
}

/**
 * Browser Purchase + processPayment_test CAPI (myfastrx pattern).
 * eventID / eventId = raw orderId on both sides for dedupe.
 */
export async function trackMetaPurchase({
  orderId,
  value,
  currency = "USD",
  email,
  phone,
  firstName,
  lastName,
  city,
  state,
  zip,
  country,
  contentIds = [],
  contentName,
  eventSourceUrl,
} = {}) {
  if (typeof window === "undefined") {
    console.warn("[Meta] Purchase skipped: no window");
    return false;
  }
  if (!orderId) {
    console.warn("[Meta] Purchase skipped: missing orderId");
    return false;
  }

  const eventId = getMetaPurchaseEventId(orderId);
  if (!eventId) {
    console.warn("[Meta] Purchase skipped: empty eventId");
    return false;
  }
  if (metaPurchaseAlreadyFired(eventId)) {
    console.log(
      "[Meta] Purchase skipped: already fired",
      eventId,
      "— dedupe OK. Clear sessionStorage meta_purchase_* for a fresh test."
    );
    return false;
  }

  const numericValue = parseFloat(value);
  if (!Number.isFinite(numericValue)) {
    console.warn("[Meta] Purchase skipped: invalid value", value);
    return false;
  }

  const amount = Math.round(numericValue * 100) / 100;
  const { fbp, fbc } = getMetaClickIds();

  const [hashedEmail, hashedPhone] = await Promise.all([
    hashEmailForMeta(email),
    hashPhoneForMeta(phone),
  ]);

  // --- Browser Pixel (same eventID as CAPI) ---
  try {
    await waitForFbq();

    try {
      // Advanced Matching: Meta accepts pre-hashed SHA-256 (normalized) values
      const userData = {};
      if (hashedEmail) userData.em = hashedEmail;
      if (hashedPhone) userData.ph = hashedPhone;
      if (firstName) userData.fn = normalizeName(firstName);
      if (lastName) userData.ln = normalizeName(lastName);
      if (city) userData.ct = normalizeCity(city);
      if (state) userData.st = normalizeState(state);
      if (zip) userData.zp = normalizeZip(zip);
      if (country) userData.country = normalizeCountry(country);
      if (fbc) userData.fbc = fbc;
      if (fbp) userData.fbp = fbp;

      if (Object.keys(userData).length > 0) {
        window.fbq("init", META_PIXEL_ID, userData);
      }
    } catch (err) {
      console.warn("[Meta] Advanced Matching init failed", err);
    }

    const pixelPayload = {
      value: amount,
      currency: currency || "USD",
      content_type: "product",
    };
    if (contentName) pixelPayload.content_name = contentName;
    if (contentIds?.length) {
      pixelPayload.content_ids = contentIds.map(String);
    }

    const eventOptions = { eventID: eventId };
    const optionUserData = {};
    if (hashedEmail) optionUserData.em = hashedEmail;
    if (hashedPhone) optionUserData.ph = hashedPhone;
    if (fbc) optionUserData.fbc = fbc;
    if (fbp) optionUserData.fbp = fbp;
    if (Object.keys(optionUserData).length > 0) {
      eventOptions.userData = optionUserData;
    }

    // Primary: standard Purchase event (NOT custom)
    window.fbq("track", "Purchase", pixelPayload, eventOptions);
    try {
      window.fbq(
        "trackSingle",
        META_PIXEL_ID,
        "Purchase",
        pixelPayload,
        eventOptions
      );
    } catch {
      /* older fbq builds may not support trackSingle */
    }

    console.log("[Meta] Purchase pixel fired", {
      event_name: "Purchase",
      eventID: eventId,
      value: amount,
      currency: currency || "USD",
      hasHashedEmail: !!hashedEmail,
      hasHashedPhone: !!hashedPhone,
      fbp: fbp || "(missing)",
      fbc: fbc || "(missing)",
    });
  } catch (err) {
    console.error("[Meta] Purchase pixel failed", err);
  }

  // Always send a visible tr/?ev=Purchase beacon (easy to find in Network)
  fireMetaPurchaseBeacon({
    eventId,
    amount,
    currency: currency || "USD",
    contentIds,
    fbp,
    fbc,
    hashedEmail,
    hashedPhone,
  });

  // --- Server CAPI via processPayment_test (same event_name + eventId) ---
  // Panel expects plaintext email/phone and hashes server-side (same as myfastrx).
  try {
    await sendMetaCapiPurchase({
      eventId,
      amount,
      currency: currency || "USD",
      email,
      phone,
      contentIds,
      contentName,
      eventSourceUrl:
        eventSourceUrl ||
        (typeof window !== "undefined" ? window.location.href : ""),
    });
  } catch (err) {
    console.error("[Meta CAPI] processPayment_test error", err);
  }

  markMetaPurchaseFired(eventId);
  clearPendingMetaPurchase();
  console.log("[Meta] Purchase conversion complete", {
    event_name: "Purchase",
    eventId,
    amount,
  });
  return true;
}
